/**
 * KanbanBoard - Unified task management board with drag-and-drop
 * Rewired: 3-column flow (todo → in_progress → done) using local tasks table.
 * @atlaskit/pragmatic-drag-and-drop, filtering, task detail panel, inline creation.
 */
'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowRight, ArrowLeft, Timer, AlertTriangle, X, Plus, GripVertical, Filter, Pencil, Trash2 } from 'lucide-react';
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { usePolling } from '@/lib/usePolling';
import { useToast } from '@/components/ToastProvider';
import type { KanbanTask, PriorityLevel, PriorityTheme } from '@/lib/types';
import { Select, Textarea , Button, Input } from '@/components/ui';
import { dueLabel, todayCentral } from '@/lib/shop-board/due-date';
import { useConfirm } from '@/hooks/useConfirm';

const STATUS_FLOW = ['todo', 'in_progress', 'done'];

const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  done: 'Done',
};
export const KANBAN_COLUMN_RENDER_LIMIT = 50;

const PRIORITY_THEMES: Record<PriorityLevel, PriorityTheme> = {
  P0: { color: 'text-rose-500', bg: 'bg-rose-500/10', border: 'border-rose-500/50' },
  P1: { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/50' },
  P2: { color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/50' },
  P3: { color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/50' },
};

const DEFAULT_THEME: PriorityTheme = { color: 'text-zinc-500', bg: 'bg-zinc-500/10', border: 'border-zinc-500/50' };

// ─── Draggable Task Card ───

interface TaskCardProps {
  task: KanbanTask;
  onMoveToStatus: (_id: string, _targetStatus: string) => void;
  onSelect: (_task: KanbanTask) => void;
  isDragging: boolean;
}

const TaskCard = React.memo(function TaskCard({ task, onMoveToStatus, onSelect, isDragging }: TaskCardProps) {
  const { confirm: _confirm, ConfirmDialogElement: _ConfirmDialogElement } = useConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const theme = PRIORITY_THEMES[task.priority as PriorityLevel] || DEFAULT_THEME;
  const metrics = task.metrics as Record<string, number>;
  const hasPain = (metrics?.wall_seconds ?? 0) > 600 || (metrics?.retries ?? 0) > 0;
  const currentIdx = STATUS_FLOW.indexOf(task.status);
  const isDone = task.status === 'done';
  const canGoBack = currentIdx > 0;
  const canGoForward = currentIdx < STATUS_FLOW.length - 1;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return draggable({
      element: el,
      getInitialData: () => ({ taskId: task.id, currentStatus: task.status }),
    });
  }, [task.id, task.status]);

  return (
    <div
      ref={ref}
      className={`bg-zinc-950 border ${hasPain ? 'border-rose-500/30 shadow-[0_0_15px_rgba(244,63,94,0.1)]' : 'border-white/5'} rounded-xl p-4 mb-3 hover:border-zinc-700 transition-colors group ${isDragging ? 'opacity-40 scale-95' : ''} ${isDone ? 'opacity-60' : ''}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <div className={isDone ? 'text-zinc-700' : 'cursor-grab active:cursor-grabbing text-zinc-300 hover:text-white transition-colors'}>
            <GripVertical className="w-3.5 h-3.5" />
          </div>
          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${theme.bg} ${theme.color} ${theme.border}`}>
            {task.priority || 'P2'}
          </span>
          <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-tighter">
            {task.id}
          </span>
        </div>
        <div className="flex items-center gap-0.5">
          {canGoBack && (
            <Button variant="ghost"
              onClick={(e) => { e.stopPropagation(); onMoveToStatus(task.id, STATUS_FLOW[currentIdx - 1]); }}
              className="text-zinc-300 hover:text-white transition-colors p-1"
              title={`Move to ${STATUS_FLOW[currentIdx - 1]}`}
            >
              <ArrowLeft className="w-3 h-3" />
            </Button>
          )}
          {canGoForward && (
            <Button variant="ghost"
              onClick={(e) => { e.stopPropagation(); onMoveToStatus(task.id, STATUS_FLOW[currentIdx + 1]); }}
              className="text-zinc-300 hover:text-white transition-colors p-1"
              title={`Move to ${STATUS_FLOW[currentIdx + 1]}`}
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <Button variant="ghost" onClick={() => onSelect(task)} className="text-left w-full p-0 hover:bg-transparent">
        <h4 className="text-sm font-medium text-zinc-200 leading-snug mb-4 group-hover:text-white transition-colors">
          {task.title}
        </h4>
      </Button>

      <div className="flex items-center justify-between pt-3 border-t border-white/5">
        <div className="flex items-center gap-3">
          <div className={`w-1.5 h-1.5 rounded-full ${task.status === 'done' ? 'bg-emerald-500' : task.status === 'in_progress' ? 'bg-amber-500 animate-pulse' : 'bg-zinc-800'}`} />
          <span className="text-[10px] text-zinc-400 font-mono italic">@{task.assignee}</span>
        </div>
        <div className="flex items-center gap-2">
          {task.due_date != null && (() => {
            const due = dueLabel(task.due_date, todayCentral());
            return (
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${due.overdue && !isDone ? 'bg-rose-500/15 text-rose-400' : 'bg-zinc-800 text-zinc-400'}`}>
                due {due.text}
              </span>
            );
          })()}
          {task.dept && (
            <span className="text-[10px] font-mono text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded">{task.dept}</span>
          )}
          {metrics?.wall_seconds != null && (
            <div className={`flex items-center gap-1 font-mono text-[10px] ${hasPain ? 'text-rose-500' : 'text-zinc-500'}`}>
              <Timer className="w-3 h-3" />
              {metrics.wall_seconds}s
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// ─── Drop Target Column ───

interface ColumnProps {
  status: string;
  tasks: KanbanTask[];
  onMoveToStatus: (_id: string, _targetStatus: string) => void;
  onSelectTask: (_task: KanbanTask) => void;
  draggingTaskId: string | null;
  isOver: boolean;
  showCreateForm: boolean;
  onToggleCreate: () => void;
  onCreateTask: (_title: string, _priority: string, _dept: string, _dueDate: string | null) => void;
}

const Column = React.memo(function Column({ status, tasks, onMoveToStatus, onSelectTask, draggingTaskId, isOver, showCreateForm, onToggleCreate, onCreateTask }: ColumnProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const visibleTasks = showAllTasks ? tasks : tasks.slice(0, KANBAN_COLUMN_RENDER_LIMIT);
  const hiddenTaskCount = Math.max(0, tasks.length - visibleTasks.length);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return dropTargetForElements({
      element: el,
      getData: () => ({ targetStatus: status }),
      canDrop: ({ source }) => source.data.currentStatus !== status,
    });
  }, [status]);

  return (
    <div
      ref={ref}
      className={`flex-1 w-full md:min-w-[280px] flex flex-col bg-zinc-900/30 border-b md:border-b-0 md:border-r border-white/5 last:border-0 last:border-r-0 transition-colors ${isOver ? 'bg-blue-500/5' : ''}`}
    >
      <div className="p-4 border-b border-white/5 flex justify-between items-center bg-zinc-950/20 backdrop-blur-md">
        <p className="text-[10px] font-black text-white uppercase tracking-[0.2em]">{STATUS_LABELS[status] ?? status.replace('_', ' ')}</p>
        <div className="flex items-center gap-2">
          {status === 'todo' && (
            <Button variant="ghost"
              onClick={onToggleCreate}
              className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors text-[10px] ${showCreateForm ? 'border-white text-white bg-white/10' : 'border-zinc-700 text-zinc-500 hover:text-white hover:border-white'}`}
            >
              {showCreateForm ? <X className="w-2.5 h-2.5" /> : '+'}
            </Button>
          )}
          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">
            {tasks.length}
          </span>
        </div>
      </div>

      <div className="flex-1 p-3 overflow-y-auto max-h-[400px] md:max-h-[600px]">
        {showCreateForm && status === 'todo' && (
          <InlineCreateForm onSubmit={onCreateTask} onCancel={onToggleCreate} />
        )}
        {isOver && (
          <div className="border-2 border-dashed border-blue-500/40 rounded-xl p-4 mb-3 flex items-center justify-center">
            <span className="text-[10px] text-blue-400 font-mono uppercase">Drop here</span>
          </div>
        )}
        {visibleTasks.map(t => (
          <TaskCard
            key={t.id}
            task={t}
            onMoveToStatus={onMoveToStatus}
            onSelect={onSelectTask}
            isDragging={draggingTaskId === t.id}
          />
        ))}
        {hiddenTaskCount > 0 && (
          <Button
            variant="ghost"
            onClick={() => setShowAllTasks(true)}
            className="w-full rounded-lg border border-white/5 bg-white/[0.02] py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 transition-colors hover:border-white/10 hover:text-zinc-300"
          >
            Load {hiddenTaskCount} more
          </Button>
        )}
        {tasks.length === 0 && !showCreateForm && (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-600">
            <p className="text-xs font-medium">No tasks</p>
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Inline Create Form ───

const InlineCreateForm = ({ onSubmit, onCancel }: { onSubmit: (_title: string, _priority: string, _dept: string, _dueDate: string | null) => void; onCancel: () => void }) => {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('P2');
  const [dept, setDept] = useState('SHARED');
  const [dueDate, setDueDate] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSubmit(title.trim(), priority, dept, dueDate || null);
    setTitle('');
    setDueDate('');
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 mb-3">
      <Textarea
        ref={inputRef}
        value={title}
        onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } if (e.key === 'Escape') onCancel(); }}
        placeholder="Task title..."
        rows={2}
      />
      <div className="flex items-center gap-2 mb-2">
        <Select value={priority} onChange={e => setPriority(e.target.value)}>
          <option value="P0">P0</option>
          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="P3">P3</option>
        </Select>
        <Select value={dept} onChange={e => setDept(e.target.value)}>
          <option value="SHARED">SHARED</option>
          <option value="SHOP">SHOP</option>
          <option value="MARKETING">MARKETING</option>
          <option value="FINANCE">FINANCE</option>
          <option value="GROWTH">GROWTH</option>
          <option value="OPS">OPS</option>
          <option value="TECHNOLOGY">TECHNOLOGY</option>
        </Select>
      </div>
      <div className="mb-2">
        <Input type="date" aria-label="Due date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} className="text-[10px] text-zinc-300 hover:text-white transition-colors px-2 py-1">Cancel</Button>
        <Button variant="ghost" onClick={handleSubmit} disabled={!title.trim()} className="text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-30 disabled:hover:bg-blue-600 rounded px-3 py-1 transition-colors flex items-center gap-1">
          <Plus className="w-3 h-3" /> Create
        </Button>
      </div>
    </div>
  );
};

// ─── Task Detail Panel ───

const TaskDetail = ({ task, onClose, onMoveToStatus, onEdit, onDelete }: {
  task: KanbanTask;
  onClose: () => void;
  onMoveToStatus: (_id: string, _targetStatus: string) => void;
  onEdit: (_id: string, _updates: Record<string, string>) => void;
  onDelete: (_id: string) => void;
}) => {
  const { confirm, ConfirmDialogElement: detailConfirmEl } = useConfirm();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editPriority, setEditPriority] = useState(task.priority || 'P2');
  const [editDept, setEditDept] = useState(task.dept || 'SHARED');
  const [saving, setSaving] = useState(false);
  const theme = PRIORITY_THEMES[task.priority as PriorityLevel] || DEFAULT_THEME;
  const metrics = task.metrics as Record<string, number>;

  async function handleSave() {
    setSaving(true);
    onEdit(task.id, { title: editTitle, priority: editPriority, dept: editDept });
    setSaving(false);
    setIsEditing(false);
  }

  async function handleDelete() {
    if (!(await confirm({ title: 'Delete this task? It will be archived and hidden from the board.' }))) return;
    onDelete(task.id);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true" tabIndex={-1} onClick={onClose} onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') onClose(); }}>
      <div className="bg-zinc-950 border border-white/10 rounded-2xl w-full max-w-lg mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-white/5 flex justify-between items-start">
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-3">
                <Textarea
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  rows={2}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Select value={editPriority} onChange={e => setEditPriority(e.target.value)}>
                    <option value="P0">P0</option><option value="P1">P1</option><option value="P2">P2</option><option value="P3">P3</option>
                  </Select>
                  <Select value={editDept} onChange={e => setEditDept(e.target.value)}>
                    <option value="SHARED">SHARED</option><option value="SHOP">SHOP</option><option value="MARKETING">MARKETING</option><option value="FINANCE">FINANCE</option><option value="GROWTH">GROWTH</option><option value="OPS">OPS</option><option value="TECHNOLOGY">TECHNOLOGY</option>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setIsEditing(false)} className="text-xs text-zinc-400 hover:text-zinc-200 px-2 py-1">Cancel</Button>
                  <Button variant="ghost" onClick={handleSave} disabled={saving || !editTitle.trim()} className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded font-medium disabled:opacity-50">
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${theme.bg} ${theme.color} ${theme.border}`}>
                    {task.priority || 'P2'}
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase">{task.id}</span>
                  {task.dept && <span className="text-[10px] font-mono text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded">{task.dept}</span>}
                </div>
                <h3 className="text-lg font-bold text-white leading-snug">{task.title}</h3>
              </>
            )}
          </div>
          <div className="flex items-center gap-1 ml-3 flex-shrink-0">
            {!isEditing && (
              <Button variant="ghost" onClick={() => setIsEditing(true)} className="text-zinc-500 hover:text-white transition-colors p-1" title="Edit task" aria-label="Edit task">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button variant="ghost" onClick={handleDelete} className="text-zinc-500 hover:text-rose-400 transition-colors p-1" title="Delete task" aria-label="Delete task">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" onClick={onClose} className="text-zinc-300 hover:text-white transition-colors p-1" aria-label="Close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Status</span>
              <Select
                value={task.status}
                onChange={e => onMoveToStatus(task.id, e.target.value)}
              >
                {STATUS_FLOW.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Assignee</span>
              <span className="text-sm text-zinc-200 font-mono">@{task.assignee}</span>
            </div>
          </div>

          {Object.keys(metrics).length > 0 && (
            <div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-2">Metrics</span>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(metrics).map(([k, v]) => (
                  <div key={k} className="bg-zinc-900 rounded-lg px-3 py-2">
                    <span className="text-[10px] text-zinc-500 font-mono uppercase block">{k}</span>
                    <span className="text-sm text-zinc-200 font-mono">{typeof v === 'number' ? v.toLocaleString() : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {detailConfirmEl}
    </div>
  );
};

// ─── Filter Bar ───

const FilterBar = ({
  departments, priorities, selectedDepts, selectedPriorities, onToggleDept, onTogglePriority, totalCount, filteredCount,
}: {
  departments: string[];
  priorities: string[];
  selectedDepts: Set<string>;
  selectedPriorities: Set<string>;
  onToggleDept: (_d: string) => void;
  onTogglePriority: (_p: string) => void;
  totalCount: number;
  filteredCount: number;
}) => {
  const hasFilters = selectedDepts.size > 0 || selectedPriorities.size > 0;

  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-white/5 bg-zinc-900/20">
      <Filter className="w-3 h-3 text-zinc-400" />
      <div className="flex flex-wrap items-center gap-1.5">
        {departments.map(d => (
          <Button variant="ghost"
            key={d}
            onClick={() => onToggleDept(d)}
            className={`text-[10px] font-mono px-2 py-0.5 rounded border transition-colors ${selectedDepts.has(d) ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700'}`}
          >
            {d}
          </Button>
        ))}
      </div>
      <div className="w-px h-4 bg-zinc-800" />
      <div className="flex items-center gap-1.5">
        {priorities.map(p => {
          const pt = PRIORITY_THEMES[p as PriorityLevel] || DEFAULT_THEME;
          return (
            <Button variant="ghost"
              key={p}
              onClick={() => onTogglePriority(p)}
              className={`text-[10px] font-black px-2 py-0.5 rounded border transition-colors ${selectedPriorities.has(p) ? `${pt.bg} ${pt.color} ${pt.border}` : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300'}`}
            >
              {p}
            </Button>
          );
        })}
      </div>
      {hasFilters && (
        <span className="text-[10px] font-mono text-zinc-500 ml-auto">
          {filteredCount} / {totalCount}
        </span>
      )}
    </div>
  );
};

// ─── Main Board ───

export const KanbanBoard = () => {
  const { toast } = useToast();
  const { confirm, ConfirmDialogElement } = useConfirm();
  const [localTasks, setLocalTasks] = useState<KanbanTask[] | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<KanbanTask | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedDepts, setSelectedDepts] = useState<Set<string>>(new Set());
  const [selectedPriorities, setSelectedPriorities] = useState<Set<string>>(new Set());
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMoveRef = useRef<(_taskId: string, _targetStatus: string) => void>(() => { });

  const { data: serverTasks, error, refresh } = usePolling<KanbanTask[]>({
    url: '/api/tasks',
    intervalMs: 5000,
    transform: useCallback((json: Record<string, any>) => (json as { tasks: KanbanTask[] }).tasks, []),
    initialValue: [],
  });

  const tasks = localTasks ?? serverTasks;

  // Extract unique departments and priorities from task data
  const departments = [...new Set(tasks.map(t => t.dept).filter(Boolean))].sort();
  const priorities = ['P0', 'P1', 'P2', 'P3'];

  // Apply filters
  const filteredTasks = tasks.filter(t => {
    if (selectedDepts.size > 0 && !selectedDepts.has(t.dept)) return false;
    if (selectedPriorities.size > 0 && !selectedPriorities.has(t.priority)) return false;
    return true;
  });

  const toggleDept = (d: string) => {
    setSelectedDepts(prev => {
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  };

  const togglePriority = (p: string) => {
    setSelectedPriorities(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  // Clear error timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  const setTimedError = useCallback((msg: string) => {
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    setMoveError(msg);
    errorTimeoutRef.current = setTimeout(() => setMoveError(null), 4000);
  }, []);

  // ── Drag-and-drop monitor ──
  useEffect(() => {
    return combine(
      monitorForElements({
        onDragStart: ({ source }) => {
          setDraggingTaskId(source.data.taskId as string);
        },
        onDrag: ({ location }) => {
          const target = location.current.dropTargets[0];
          setOverColumn(target ? (target.data.targetStatus as string) : null);
        },
        onDrop: ({ source, location }) => {
          setDraggingTaskId(null);
          setOverColumn(null);
          const target = location.current.dropTargets[0];
          if (!target) return;
          const taskId = source.data.taskId as string;
          const targetStatus = target.data.targetStatus as string;
          const currentStatus = source.data.currentStatus as string;
          if (targetStatus !== currentStatus) {
            handleMoveRef.current(taskId, targetStatus);
          }
        },
      })
    );
  }, []);

  const handleMoveToStatus = async (taskId: string, targetStatus: string) => {
    setMoveError(null);
    const previousTasks = tasks;
    const optimistic = tasks.map(t => t.id === taskId ? { ...t, status: targetStatus } : t);
    setLocalTasks(optimistic);

    // Update selected task if it's the one being moved
    if (selectedTask?.id === taskId) {
      setSelectedTask({ ...selectedTask, status: targetStatus });
    }

    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, newStatus: targetStatus }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      await refresh();
      setLocalTasks(null);
    } catch (err) {
      setLocalTasks(previousTasks);
      if (selectedTask?.id === taskId) {
        setSelectedTask(selectedTask);
      }
      toast(`Failed to move task. ${err instanceof Error ? err.message : ''}`, 'error');
      setTimedError(`Failed to move task. ${err instanceof Error ? err.message : ''}`);
    }
  };
  handleMoveRef.current = handleMoveToStatus;

  const handleCreateTask = async (title: string, priority: string, dept: string, dueDate: string | null) => {
    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: 'NEW', newStatus: title, priority, dept, due_date: dueDate }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      toast('Task created');
      setShowCreateForm(false);
      await refresh();
      setLocalTasks(null); // Force refresh from server
    } catch (err) {
      toast(`Failed to create task. ${err instanceof Error ? err.message : ''}`, 'error');
      setTimedError(`Failed to create task. ${err instanceof Error ? err.message : ''}`);
    }
  };

  const handleEditTask = async (taskId: string, updates: Record<string, string>) => {
    const previousTasks = tasks;
    const optimistic = tasks.map(t => t.id === taskId ? { ...t, ...updates } : t);
    setLocalTasks(optimistic);
    if (selectedTask?.id === taskId) setSelectedTask({ ...selectedTask, ...updates } as KanbanTask);
    try {
      const res = await fetch(`/api/tasks?id=${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      await refresh();
      setLocalTasks(null);
    } catch {
      setLocalTasks(previousTasks);
      toast('Failed to edit task', 'error');
      setTimedError('Failed to edit task.');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!(await confirm({ title: 'Delete this task?' }))) return;
    const previousTasks = tasks;
    setLocalTasks(tasks.filter(t => t.id !== taskId));
    try {
      const res = await fetch(`/api/tasks?id=${taskId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      toast('Task deleted');
      await refresh();
      setLocalTasks(null);
    } catch {
      setLocalTasks(previousTasks);
      toast('Failed to delete task', 'error');
      setTimedError('Failed to delete task.');
    }
  };

  return (
    <div className="bg-zinc-950 border border-white/5 rounded-2xl shadow-2xl overflow-hidden mb-12">
      {/* Header */}
      <div className="p-4 bg-zinc-900/50 border-b border-white/5 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Task Board</span>
        </div>
        <div className="flex items-center gap-4">
          {moveError && (
            <span className="text-[10px] font-mono text-rose-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {moveError}
            </span>
          )}
          {error && (
            <span className="text-[10px] font-mono text-amber-500">Polling error</span>
          )}
          <span className="text-[10px] font-mono text-zinc-500 uppercase">Drag & Drop Active</span>
        </div>
      </div>

      {/* Filter Bar */}
      {departments.length > 0 && (
        <FilterBar
          departments={departments}
          priorities={priorities}
          selectedDepts={selectedDepts}
          selectedPriorities={selectedPriorities}
          onToggleDept={toggleDept}
          onTogglePriority={togglePriority}
          totalCount={tasks.length}
          filteredCount={filteredTasks.length}
        />
      )}

      {/* Columns */}
      <div className="flex flex-col md:flex-row overflow-x-auto min-h-[400px]">
        {STATUS_FLOW.map(status => (
          <Column
            key={status}
            status={status}
            tasks={filteredTasks.filter(t => t.status === status)}
            onMoveToStatus={handleMoveToStatus}
            onSelectTask={setSelectedTask}
            draggingTaskId={draggingTaskId}
            isOver={overColumn === status}
            showCreateForm={showCreateForm && status === 'todo'}
            onToggleCreate={() => setShowCreateForm(prev => !prev)}
            onCreateTask={handleCreateTask}
          />
        ))}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetail
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onMoveToStatus={handleMoveToStatus}
          onEdit={handleEditTask}
          onDelete={handleDeleteTask}
        />
      )}
        {ConfirmDialogElement}
</div>
  );
};
